import React, { useState } from 'react';
import { useAuth } from '../../../../AuthContext';
import { useProfileSheet } from '../../../../contexts/ProfileSheetContext';
import SheetField from '../shared/SheetField';
import SheetMessage from '../shared/SheetMessage';

interface LoginViewProps {
  /** One-shot banner handed over from a completed create/reset-password flow. */
  notice?: string | null;
}

const LoginView: React.FC<LoginViewProps> = ({ notice }) => {
  const { login } = useAuth();
  const { setView } = useProfileSheet();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      setError('Enter your email/username and password');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await login(identifier.trim(), password);
      // Success handling (close / switch to profile) lives in ProfileSheet's
      // auth-success effect so cross-tab logins behave identically.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {notice && <SheetMessage kind="ok">{notice}</SheetMessage>}
      {error && <SheetMessage kind="err">{error}</SheetMessage>}
      <SheetField
        label="Email or username"
        value={identifier}
        onChange={setIdentifier}
        name="username"
        autoComplete="username"
        disabled={submitting}
        autoFocus
      />
      <SheetField
        label="Password"
        value={password}
        onChange={setPassword}
        type="password"
        name="password"
        autoComplete="current-password"
        disabled={submitting}
      />
      <button
        type="submit"
        className="tend-btn tend-btn--cta tend-btn--block"
        disabled={submitting}
        style={{ marginTop: 4 }}
      >
        {submitting ? 'Signing in…' : 'Sign In'}
      </button>
      <div className="tend-links-row">
        <button type="button" className="tend-link" onClick={() => setView('forgot')}>
          Forgot password?
        </button>
        <button type="button" className="tend-link" onClick={() => setView('register')}>
          Create account
        </button>
      </div>
    </form>
  );
};

export default LoginView;
